import { CdkDrag, CdkDragDrop, CdkDragEnd, CdkDragPlaceholder, CdkDragPreview, CdkDragStart, CdkDropList } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Inject,
    Input,
    LOCALE_ID,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { faEraser, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { SortDirections } from '@hub4b/constantes/sort-directions';
import { DraggedRowPreviewComponent } from '@hub4b/controls/datas-list/dragged-row-preview/dragged-row-preview.component';
import { ExpandedCommentCellComponent } from '@hub4b/controls/datas-list/expanded-comment-cell/expanded-comment-cell.component';
import { ExpandedDetailCellComponent } from '@hub4b/controls/datas-list/expanded-detail-cell/expanded-detail-cell.component';
import { ContextualActionType } from '@hub4b/controls/datas-list/table-action-cell/models/contextual-action-type';
import { ContextualMenuOpenEvent } from '@hub4b/controls/datas-list/table-action-cell/models/contextual-menu-open-event';
import { ContextualMenuSelectEvent } from '@hub4b/controls/datas-list/table-action-cell/models/contextual-menu-select-event';
import { TableActionCellComponent } from '@hub4b/controls/datas-list/table-action-cell/table-action-cell.component';
import { TableCellComponent } from '@hub4b/controls/datas-list/table-cell/table-cell.component';
import { TableHeaderCellComponent } from '@hub4b/controls/datas-list/table-header-cell/table-header-cell.component';
import { TableWorkflowCellComponent } from '@hub4b/controls/datas-list/table-workflow-cell/table-workflow-cell.component';
import { BaseDirective } from '@hub4b/directives/base.directive';
import { ColumnResizeDirective } from '@hub4b/directives/column-resize.directive';
import { FormFocusDirective } from '@hub4b/directives/form-focus.directive';
import { ArrayHelper } from '@hub4b/helpers/array-helper';
import { DateHelper } from '@hub4b/helpers/date-helper';
import { DownloadHelper } from '@hub4b/helpers/download-helper';
import { DragContext } from '@hub4b/models/drag-context';
import { ColumnType } from '@hub4b/models/enums/column-type';
import { FilterType } from '@hub4b/models/enums/filter-type';
import { NavigateType } from '@hub4b/models/enums/navigate-type';
import { PropertyType } from '@hub4b/models/enums/property-type';
import { FolderCounterUpdatedEvent } from '@hub4b/models/events/folder-counter-updated-event';
import { FolderDocumentsRefreshEvent } from '@hub4b/models/events/folder-documents-refresh-event';
import { FolderListDeletedEvent } from '@hub4b/models/events/folder-list-deleted-event';
import { FolderListUpdatedEvent } from '@hub4b/models/events/folder-list-updated-event';
import { FolderTabInfos } from '@hub4b/models/folder-tab-infos';
import { FoldersListConfiguration } from '@hub4b/models/folders-list-configuration';
import { IAllSelectable } from '@hub4b/models/interfaces/all-selectable.interface';
import { IDragable } from '@hub4b/models/interfaces/dragable.interface';
import { IFilterable } from '@hub4b/models/interfaces/filterable.interface';
import { Navigate } from '@hub4b/models/navigate';
import { PredefinedFilter } from '@hub4b/models/predefined-filter';
import { SelectionContext } from '@hub4b/models/selection-context';
import { ColumnDefinition } from '@hub4b/models/server/column-definition';
import { FilterInfos } from '@hub4b/models/server/core/filter-infos';
import { SortInfos } from '@hub4b/models/server/core/sort-infos';
import { Colors } from '@hub4b/models/server/enums/colors';
import { LockedType } from '@hub4b/models/server/enums/locked-type';
import { Rights } from '@hub4b/models/server/enums/rights';
import { ViewType } from '@hub4b/models/server/enums/view-type';
import { Folder } from '@hub4b/models/server/folder';
import { StandByDatas } from '@hub4b/models/server/stand-by-datas';
import { DialogService } from '@hub4b/services/actions/dialog.service';
import { FolderActionsService } from '@hub4b/services/actions/folder-actions.service';
import { NotificationService } from '@hub4b/services/actions/notification.service';
import { SpinnerService } from '@hub4b/services/core/spinner.service';
import { EventsManagerService } from '@hub4b/services/events/events-manager.service';
import { FolderCounterEventsService } from '@hub4b/services/events/folder-counter-events.service';
import { FolderListEventsService } from '@hub4b/services/events/folder-list-events.service';
import { FolderCommentsService } from '@hub4b/services/http/folder-comments.service';
import { FolderDocumentsService } from '@hub4b/services/http/folder-documents.service';
import { FoldersService } from '@hub4b/services/http/folders.service';
import { UsersService } from '@hub4b/services/http/users.service';
import { ConnectedUsersStateService } from '@hub4b/services/state/connected-users-state.service';
import { FolderCommentsStateService } from '@hub4b/services/state/folder-comments-state.service';
import { FolderSearchStateService } from '@hub4b/services/state/folder-search-state.service';
import { NavigationStateService } from '@hub4b/services/state/navigate-state.service';
import { ProfileSettingsStateService } from '@hub4b/services/state/profile-settings-state.service';
import { ViewsStateService } from '@hub4b/services/state/views-state.service';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { BaseDatasource } from './data-sources/base-datasource';

@Component({
    templateUrl: './base-datas-list.component.html',
    styleUrls: ['./base-datas-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        FormFocusDirective,
        MatTableModule,
        MatSortModule,
        CdkDropList,
        NgClass,
        TableHeaderCellComponent,
        TableCellComponent,
        TableWorkflowCellComponent,
        TableActionCellComponent,
        ExpandedDetailCellComponent,
        ExpandedCommentCellComponent,
        CdkDrag,
        CdkDragPlaceholder,
        CdkDragPreview,
        DraggedRowPreviewComponent,
        ColumnResizeDirective,
    ]
})
export class BaseDatasListComponent extends BaseDirective implements OnInit, IAllSelectable, IDragable<Folder>, IFilterable {
    protected folderSearchStateService = inject(FolderSearchStateService);
    public resetFilterIcon = faEraser;

    /** Déclaration des ENUM */
    public PropertyType = PropertyType;
    public Colors = Colors;

    public isLoad: boolean = true;
    public faLoad = faSpinner;

    @HostListener('document:contextmenu', ['$event'])
    onDocumentContextMenu(event: MouseEvent) {
        // Désactive l'utilisation du clic-droit sur le navigateur
        event.preventDefault();
    }

    /** Emis lorsque l'utilisateur veut afficher le détail d'un dossier. */
    @Output()
    folderDisplayed: EventEmitter<FolderTabInfos> = new EventEmitter();

    /**
     * Valeur indiquant si les commentaires sont affichés (au lieu des documents) lorsque l'on déplie un dossier.
     */
    @Input()
    public isWorkflowStepDateVisible: boolean = false;

    /**
     * Valeur indiquant si les dates de changement d'état des étapes de workflow sont visibles.
     */
    @Input()
    public expandComments: boolean = false;

    /* Reference au conteneur scrollable */
    @ViewChild('resultsContainer', { read: ElementRef })
    resultsContainerElementRef: ElementRef;

    private configuration$ = new BehaviorSubject<FoldersListConfiguration>(null);

    public _internalConfiguration: FoldersListConfiguration;

    @Input()
    set configuration(value) {
        this.configuration$.next(value);
    }

    get configuration() {
        return this.configuration$.getValue();
    }

    /** Liste des dossiers chargés */
    public folders: Folder[] = null;

    /* Nombre de dossiers chargés à l'écran */
    public loadedFoldersCount: number = -1;

    /* Nombre total de dossier correspondant aux filtres courants */
    public totalFoldersCount: number = -1;

    public tableDataSource: MatTableDataSource<any> = new MatTableDataSource();
    paginator: MatPaginator = new MatPaginator(new MatPaginatorIntl(), this.cdRef);

    loadedRecordsCount: number = 50;

    /** Valeur du filtre courant. */
    public currentSort: Sort = null;

    /** Valeur indiquant si la vue affichée est une vue 'Archive' */
    public isArchiveMode: boolean = false;

    /** Filtre prédéfini courant */
    private currentPredefinedFilter: PredefinedFilter = null;

    /** Définitions des colonnes */
    displayedColumnsDefinitions: ColumnDefinition[];

    stickyColumnsDefinition: ColumnDefinition[];

    scrollableColumnsDefinition: ColumnDefinition[];

    /** Definition de la colonne 'Action'. */
    actionColumnDefinition: ColumnDefinition;

    /** Liste des noms des colonnes affichées dans le tableau de dossiers. */
    displayedColumnCodes: string[];

    /** Indique s'il est permis de déplier plusieurs lignes en même temps. */
    allowMultipleExpandRows: boolean = true;

    /** Contexte relatif à la selection des dossiers. */
    public selectionContext: SelectionContext;

    /** Contexte relatif au drag&drop de dossiers. */
    public dragContext: DragContext<Folder>;

    /** Contexte relatif aux filtres de dossiers. */
    filterInfos: FilterInfos[] = null;

    generalFilterInfos: FilterInfos[] = null;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected onConfigurationLoaded$: Subject<void> = new Subject<void>();

    protected cancelPreviousRequest$: Subject<void> = new Subject<void>();

    public moveFolderToBoxRight: boolean = false;

    public mergeFoldersRight: boolean = false;

    /**
     * Classe à appliquer à la table.
     */
    public tableClass: string = '';

    private scrollingInProgress: boolean = false;

    protected lastScrollTop: number = 0;

    protected currentScrollTop: number = 0;

    protected currentScrollLeft: number = 0;

    private configurationLoaded$: Subject<number> = new Subject<number>();

    private filtersDefined$: Subject<number> = new Subject<number>();

    public viewType: ViewType;

    /**
     * Constructeur
     * @param usersService : Service de gestion des utilisateurs.
     * @param foldersService : Service de gestion des dossiers.
     * @param navigateService : Service de gestion de la navigation entre dossiers.
     * @param folderSearchStateService : Service de persistence de l'état du formulaire de recherche.
     * @param folderActionsService : Service des actions relatives aux dossiers.
     * @param dialogService : Service de boite de dialogue.
     * @param notificationService : Service de notification.
     * @param cdRef : Detecteur de changement.
     * @param foldersTableRef : Référence du tableau de dossiers dans le DOM.
     *
     */
    constructor(
        protected connectedUserStateService: ConnectedUsersStateService,
        protected usersService: UsersService,
        protected foldersService: FoldersService,
        protected navigateService: NavigationStateService,
        protected folderActionsService: FolderActionsService,
        protected folderCommentStateService: FolderCommentsStateService,
        protected dialogService: DialogService,
        protected notificationService: NotificationService,
        protected foldersCommentsService: FolderCommentsService,
        protected cdRef: ChangeDetectorRef,
        protected foldersTableRef: ElementRef,
        protected datasource: BaseDatasource,
        protected spinner: SpinnerService,
        protected viewsStateService: ViewsStateService,
        protected folderListEventsService: FolderListEventsService,
        protected eventsManagerService: EventsManagerService,
        protected folderDocumentsService: FolderDocumentsService,
        protected folderCounterEventsService: FolderCounterEventsService,
        protected downloadHelper: DownloadHelper,
        protected profileSettingsStateService: ProfileSettingsStateService,
        protected dateHelper: DateHelper,
        @Inject(LOCALE_ID) protected locale: string
    ) {
        super();
        this.selectionContext = new SelectionContext(this.cdRef, this);
        this.dragContext = new DragContext<Folder>(this.cdRef, this.selectionContext, this, this.dialogService);

        this.moveFolderToBoxRight = this.connectedUserStateService.hasRight(Rights.MoveFolderToBox);
        this.mergeFoldersRight = this.connectedUserStateService.hasRight(Rights.MergeFolder);
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.subscribeToFolderUpdateEvent();
        this.subscribeToFolderDeleteEvent();
        this.subscribeToFolderDocumentsRefreshEvent();
        this.subscribeToFolderListRefreshEvent();
        this.subscribeToFilterContextChangeEvent();
        this.subscribeToNavigationEvent();
        this.subscribeToFolderListExportEvent();
        this.subscribeToConfigurationChangeEvent();

        /** Attend que la configuration soit chargé et que les filtres soit définis avant d'appliquer les filtres */
        combineLatest(this.configurationLoaded$, this.filtersDefined$)
            .pipe(
                map(([configurationLoaded, filterApplied]) => ({ configurationLoaded, filterApplied })),
                filter((x) => x.configurationLoaded === x.filterApplied)
            )
            .subscribe((x) => {
                this.applyFilters();
                this.cdRef.markForCheck();
            });

        this.paginator.ngOnInit();
    }

    /**
     * S'abonne aux evenements de changement de configuration.
     */
    private subscribeToConfigurationChangeEvent() {
        this.configuration$.subscribe((x: FoldersListConfiguration) => {
            if (x) {
                this._internalConfiguration = x;
                this.loadedFoldersCount = -1;
                this.totalFoldersCount = -1;

                this.tableClass = x.viewType === ViewType.Workflow ? 'workflow-folders' : 'Box-folders';
                this.viewType = x.viewType;

                this.displayedColumnsDefinitions = this._internalConfiguration.columnDefinitions;

                var savedColumnsDefinitionsJson = localStorage.getItem(this.getLocalStorageKey(this._internalConfiguration.viewId, this._internalConfiguration.listLayout.id));

                if (savedColumnsDefinitionsJson) {
                    var savedColumnsDefinitions = ColumnDefinition.fromArray(JSON.parse(savedColumnsDefinitionsJson));

                    if (!this.hasLayoutBeModified(this.displayedColumnsDefinitions, savedColumnsDefinitions)) {
                        this.displayedColumnsDefinitions = savedColumnsDefinitions;
                    } else {
                        savedColumnsDefinitions.forEach((savedColumn) => {
                            let serverColumn = this.displayedColumnsDefinitions.find((x) => x.propertyCode === savedColumn.propertyCode);
                            if (serverColumn) {
                                serverColumn.size = savedColumn.size;
                            }
                        });

                        // Sauvegarde de la taille dans le local storage.
                        localStorage.setItem(
                            this.getLocalStorageKey(this._internalConfiguration.viewId, this._internalConfiguration.listLayout.id),
                            JSON.stringify(this.displayedColumnsDefinitions)
                        );

                        this.notificationService.displayWarning(
                            'Attention',
                            "Des modifications ont été apportées à la mise en page de cette vue. Cela peut impacter l'affichage de vos colonnes."
                        );
                    }
                }

                this.stickyColumnsDefinition = this.displayedColumnsDefinitions.filter((x) => x.isStickyColumn === true);

                this.scrollableColumnsDefinition = this.displayedColumnsDefinitions.filter((x) => x.isStickyColumn === false);

                // Récupère la liste des noms de colonnes à afficher.
                this.displayedColumnCodes = this.displayedColumnsDefinitions.map((y) => y.propertyCode);

                // Récupère la définition de la colonen 'Action' et ajoute son nom dans la liste des noms de colonnes à afficher.
                this.actionColumnDefinition = this.getActionColumnDefinition();
                this.displayedColumnCodes.push(this.actionColumnDefinition.propertyCode);

                // Réinitialise la position des barres de défilement  et horizontale.
                this.scrollingInProgress = false;
                this.lastScrollTop = 0;
                this.currentScrollTop = 0;
                this.currentScrollLeft = 0;

                // Réinitialise le tri avec le tri par défaut paramétré pour la vue
                this.currentSort = {
                    active: this._internalConfiguration.attributeDefinitions.find((x) => x.id === this._internalConfiguration.listLayout.sortAttributeId).propertyCode,
                    direction: this._internalConfiguration.listLayout.sortDirection === 'asc' ? SortDirections.ASC : SortDirections.DESC,
                };

                this.afterConfigurationLoaded(x.viewId);
            }
        });
    }

    /**
     * Appelée une fois la configuration chargée.
     * @param viewId: Id de la vue.
     */
    protected afterConfigurationLoaded(viewId: number) {
        this.generalFilterInfos = [];
        this.filterInfos = [];
        this.paginator.pageSize = this.loadedRecordsCount;
        this.tableDataSource.paginator = this.paginator;
        this.configurationLoaded$.next(viewId);
        this.cdRef.markForCheck();
    }

    /**
     * Appelée une fois les dossiers chargés.
     */
    protected afterFoldersLoaded() {}

    /**
     * S'abonne aux événements de mise à jour des documents d'un dossier.
     */
    private subscribeToFolderDocumentsRefreshEvent() {
        this.folderListEventsService.folderDocumentsUpdated.pipe(takeUntil(this.componentDestroyed$)).subscribe((event: FolderDocumentsRefreshEvent) => {
            let index = this.folders.findIndex((x) => x.id === event.folderId);

            if (index !== -1) {
                // Mise à jour l'icone indiquant si le dossiers possède des documents
                const folders = this.folders.slice();
                folders[index].hasDocument = true;
                this.folders = folders;

                this.cdRef.detectChanges();
            }
        });
    }

    /**
     * S'abonne aux événements de mise à jour de dossier.
     */
    private subscribeToFolderUpdateEvent() {
        this.folderListEventsService.foldersUpdated.pipe(takeUntil(this.componentDestroyed$)).subscribe((event: FolderListUpdatedEvent) => {
            if (!!event && !!this.folders) {
                let index = this.folders.findIndex((x) => x.id === event.folder.id);

                if (index !== -1) {
                    //console.warn(`Réception de l'événement FolderListUpdatedEvent par le composant BaseDatasListComponent pour le dossier ${event.folder.id}`, event);
                    // Verifie si la bannette dans laquelle se trouve le dossier est une des bannettes sélectionnées.
                    const isListed: boolean = this.datasource.isFolderContainsIntoList(event.folder);

                    if (!isListed) {
                        // Le dossier n'appartient pas à une des bannettes sélectionnés --> on le supprime de la liste
                        this.folders = ArrayHelper.RemoveElementFromArray(this.folders.slice(), event.folder.id);
                    } else {
                        // Le dossier appartient à une des bannettes sélectionnés --> on le met à jour
                        this.folders = this.folders.slice();
                        this.folders[index] = event.folder;
                    }

                    this.fillExpandeedStatusInformation(this.folders, this.tableDataSource.data);
                    this.tableDataSource.data = this.folders;

                    this.folderCounterEventsService.displayedFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.folders.length));

                    // A faire sur un événement indiquant le drop d'un dossier.
                    this.dragContext.clearDragAndDropContext();
                    this.selectionContext.clearSelection();

                    this.cdRef.markForCheck();
                }
            }
        });
    }

    /**
     * S'abonne aux événements de Suppression de dossier.
     */
    private subscribeToFolderDeleteEvent() {
        this.folderListEventsService.foldersDeleted.pipe(takeUntil(this.componentDestroyed$)).subscribe((event: FolderListDeletedEvent) => {
            if (!!event && !!this.folders) {
                let index = this.folders.findIndex((x) => x.id === event.folderId);

                if (index !== -1) {
                    //console.warn(`Réception de l'événement FolderListDeletedEvent par le composant BaseDatasListComponent pour le dossier ${event.folderId}`, event);

                    // Le dossier est retiré de la liste
                    this.folders = ArrayHelper.RemoveElementFromArray(this.folders.slice(), event.folderId);
                    this.fillExpandeedStatusInformation(this.folders, this.tableDataSource.data);
                    this.tableDataSource.data = this.folders;

                    this.cdRef.detectChanges();
                }
            }
        });
    }

    /**
     * S'abonne aux événements de rafraichissement de la liste des dossiers.
     */
    private subscribeToFolderListRefreshEvent() {
        this.folderListEventsService.listNeedRefresh.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            //console.warn(`Réception de l'événement FolderListRefreshEvent par le composant BaseDatasListComponent`, event);
            this.applyFilters();
            this.resetScroll();
            this.cdRef.markForCheck();
        });
    }

    /**
     * S'abonne aux événements de demande d'export de la liste des dossiers.
     */
    private subscribeToFolderListExportEvent() {
        this.folderListEventsService.exportRequest.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            //console.warn(`Réception de l'événement FolderListExportEvent par le composant BaseDatasListComponent`, event);
            this.exportFolders();
        });
    }

    /**
     * S'abonne aux événements de changement de contexte de filtrage.
     */
    private subscribeToFilterContextChangeEvent() {
        this.folderSearchStateService.onPredefinedFilterChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((x) => {
            if (x) {
                this.currentPredefinedFilter = x;
                this.filtersDefined$.next(x.viewId);
                this.resetScroll();
                this.cdRef.markForCheck();
            }
        });
    }

    /**
     * S'abonne aux événements de navigation entre dossiers.
     */
    private subscribeToNavigationEvent() {
        this.navigateService.onAskNavigate$.pipe(takeUntil(this.componentDestroyed$)).subscribe((navigate) => {
            /*if (!!navigate && navigate.viewType == this.viewType) {*/
            if (!!navigate) {
                this.analyseNavigationEvent(navigate);
                if (navigate.readonly === false) {
                    this.foldersService
                        .lock(navigate.newFolder.viewId, navigate.newFolder.id)
                        .pipe(takeUntil(this.componentDestroyed$))
                        .subscribe(
                            (isValid) => {
                                this.navigateService.navigate(navigate);
                                navigate.newFolder.lockedType = LockedType.LockedByCurrentUser;
                                this.eventsManagerService.emitListFolderUpdatedEvent(navigate.view.id, navigate.newFolder);
                                this.eventsManagerService.emitFollowedFoldersUpdatedEvent(navigate.view.id, navigate.newFolder);
                            },
                            (error: HttpErrorResponse) => {
                                this.retryNavigation(navigate);
                            }
                        );
                } else {
                    this.navigateService.navigate(navigate);
                }
            }
        });
    }

    /**
     * Réessaye la navigation entre dossiers suite à une navigation sur un dossier verrouillé.
     * @param navigate : Événement de navigation.
     */
    private retryNavigation(navigate: Navigate) {
        if (navigate.navigateType === NavigateType.First) {
            // Le 1er dossier de la liste, on essaye de naviguer vers le suivant (soit le second) --> Changement du type de navigation en suivant.
            navigate.navigateType = NavigateType.Next;
        } else if (navigate.navigateType === NavigateType.Last) {
            // Le dernier dossier de la liste, on essaye de naviguer vers le precedent (soit l'avant dernier) --> Changement du type de navigation en precedent.
            navigate.navigateType = NavigateType.Previous;
        }

        // Le dossier courant devient celui verrouillé
        navigate.currentFolder = navigate.newFolder;

        // Emission d'un nouvel événement de navigation.
        this.navigateService.askNavigate(navigate);
    }

    /**
     * Analyse une demande de navigation entre dossiers.
     * @param navigate : Événement de navigation.
     */
    private analyseNavigationEvent(navigate: Navigate): void {
        // Récupération de l'id du dossier vers lequel naviguer.
        if (navigate.navigateType === NavigateType.First) {
            navigate.newFolder = this.folders[0];
        } else if (navigate.navigateType === NavigateType.Last) {
            navigate.newFolder = this.folders[this.folders.length - 1];
        } else {
            let y = navigate.navigateType === NavigateType.Previous ? -1 : 1;
            let index = this.folders.findIndex((f) => f.id === navigate.currentFolder.id);
            let newIndex = index + y;

            if (newIndex === -1 || newIndex === this.folders.length) {
                newIndex = index;
            }

            navigate.newFolder = this.folders[newIndex];
        }
    }

    /**
     * Retourne la définition de la colonne 'Action'.
     * @returns: Une définition de colonne.
     */
    private getActionColumnDefinition(): ColumnDefinition {
        const def = new ColumnDefinition();
        def.propertyCode = 'action';
        def.header = '';
        return def;
    }

    /**
     * Charge les dossiers à afficher dans le tableau.
     */
    protected loadFolders(loadDefaultFilters: boolean) {
        console.time('loadFolders');
        this.cdRef.detach();
        this.loadedRecordsCount = this._internalConfiguration.listLayout.recordsPerPage;
        this.paginator._changePageSize(this.loadedRecordsCount);

        this.cancelPreviousRequest$.next();

        // Récupère la liste des dossiers correspondants aux bannettes selectionnées.
        this.datasource
            .listDatas(0, this._internalConfiguration.listLayout.recordsPerPage, this.getSortInfos(), this.filterInfos, this.generalFilterInfos)
            .pipe(takeUntil(this.componentDestroyed$), takeUntil(this.cancelPreviousRequest$))
            .subscribe((x) => {
                let folders: Folder[] = [];

                x.value.forEach((f) => {
                    let folder = new Folder();
                    folder.copyFrom(f);
                    folder.expanded = false;
                    folders.push(folder);
                });

                this.folders = folders;

                this.loadedFoldersCount = this.folders.length;
                this.totalFoldersCount = x.totalCount;

                this.folderCounterEventsService.displayedFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.loadedFoldersCount));
                this.folderCounterEventsService.totalFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(x.totalCount));

                this.tableDataSource.paginator = this.paginator;
                this.fillExpandeedStatusInformation(this.folders, this.tableDataSource.data);
                this.tableDataSource.data = this.folders;

                this.selectionContext.clearSelection();
                this.dragContext.clearDragAndDropContext();
                this.afterFoldersLoaded();
                this.spinner.hide('folders-spinner');
                this.cdRef.reattach();
                console.timeEnd('loadFolders');
            });
    }

    /**
     * Exporte les dossiers dans un fichier EXCEL.
     */
    protected exportFolders() {
        this.spinner.show('folders-spinner');
        this.cancelPreviousRequest$.next();

        this.datasource
            .exportDatas(this.getSortInfos(), this.filterInfos, this.generalFilterInfos)
            .pipe(takeUntil(this.componentDestroyed$), takeUntil(this.cancelPreviousRequest$))
            .subscribe((blob) => {
                var fileName = this.viewsStateService.getSelectedView().name;
                var dateString = this.dateHelper.formatAsIso8601Date(new Date(Date.now()));
                this.downloadHelper.downloadFile(blob, `${fileName}-${dateString}.xlsx`);
                this.spinner.hide('folders-spinner');
            });
    }

    /**
     * Applique les filtres de recherche et/ou de colonnes.
     */
    public applyFilters() {
        this.spinner.show('folders-spinner');
        this.computeColumnsFilters();
        this.computeGeneralFilters();
        this.loadFolders(false);
    }

    /**
     * Génère le filtre de recherche
     */
    private computeGeneralFilters(): void {
        this.generalFilterInfos = [];

        if (!!this.currentPredefinedFilter.searchTerm) {
            if (!!this._internalConfiguration.attributeDefinitions) {
                this._internalConfiguration.attributeDefinitions.forEach((item) => {
                    if (item.usedBySearch === true) {
                        if (item.type == PropertyType.Text || item.type == PropertyType.Email || item.type == PropertyType.PhoneNumber) {
                            let filter: FilterInfos = {
                                columnName: item.propertyCode,
                                value: this.currentPredefinedFilter.searchTerm,
                                type: this.getColumnType(item.type),
                                filterType: this.getFilterType(item.type),
                                selectionMultiple: false,
                            };
                            this.generalFilterInfos.push(filter);
                        } else if (item.type == PropertyType.List || item.type == PropertyType.ListCustomers || item.type == PropertyType.ListUsers) {
                            let value = item.values.find((a) => a.text.toUpperCase() == this.currentPredefinedFilter.searchTerm.toUpperCase())?.text;
                            if (!!value) {
                                let filter: FilterInfos = {
                                    columnName: item.propertyCode,
                                    value: value,
                                    type: this.getColumnType(item.type),
                                    filterType: this.getFilterType(item.type),
                                    selectionMultiple: true,
                                };
                                this.generalFilterInfos.push(filter);
                            }
                        }
                    }
                });
            }
        }
    }

    /**
     * Génére le filtre des colonnes.
     */
    private computeColumnsFilters(): void {
        this.filterInfos = [];

        if (this.currentPredefinedFilter.filteredAttributes) {
            this.currentPredefinedFilter.filteredAttributes.getKeys().forEach((attributeName: string) => {
                const filteredAttribute = this.currentPredefinedFilter.filteredAttributes.get(attributeName);

                let filter: FilterInfos = {
                    columnName: attributeName,
                    value: filteredAttribute.values.join('|'),
                    type: this.getColumnType(filteredAttribute.type),
                    filterType: filteredAttribute.values?.length > 1 ? FilterType.ListMultiple : this.getFilterType(filteredAttribute.type),
                    selectionMultiple: filteredAttribute.values?.length > 1,
                };

                this.filterInfos.push(filter);
            });
        }
    }

    private getFilterType(propertyType: PropertyType): FilterType {
        let filterType: FilterType = null;
        switch (propertyType) {
            case PropertyType.Date:
                filterType = FilterType.Date;
                break;
            case PropertyType.DateAndTime:
                filterType = FilterType.Date;
                break;
            case PropertyType.List:
            case PropertyType.ListCustomers:
            case PropertyType.ListUsers:
            case PropertyType.Color:
                filterType = FilterType.ListMultiple;
                break;
            case PropertyType.Long:
            case PropertyType.Decimal:
                filterType = FilterType.Numeric;
                break;
            default:
                filterType = FilterType.Text;
                break;
        }

        return filterType;
    }

    private getColumnType(propertyType: PropertyType): ColumnType {
        let columnType: ColumnType = null;
        switch (propertyType) {
            case PropertyType.Date:
                columnType = ColumnType.Date;
                break;
            case PropertyType.DateAndTime:
                columnType = ColumnType.Date;
                break;
            case PropertyType.List:
            case PropertyType.ListCustomers:
            case PropertyType.ListUsers:
            case PropertyType.Color:
                columnType = ColumnType.List;
                break;
            case PropertyType.Long:
            case PropertyType.Decimal:
                columnType = ColumnType.Numeric;
                break;
            default:
                columnType = ColumnType.Text;
                break;
        }

        return columnType;
    }

    /** Événement lors du scroll du tableau */
    public onScroll(event) {
        const elem = event.target;
        this.currentScrollTop = elem.scrollTop;
        this.currentScrollLeft = elem.scrollLeft;

        if (
            this.scrollingInProgress === false &&
            Math.round(elem.offsetHeight + elem.scrollTop) >= elem.scrollHeight &&
            elem.scrollTop > this.lastScrollTop &&
            Math.round(elem.offsetHeight + this.lastScrollTop) < elem.scrollHeight &&
            this.loadedFoldersCount < this.totalFoldersCount
        ) {
            // Chargement de la "page suivante" si un chargement n'est pas déjà en cours & si le scrolling dépasse la hauteur de la barre de défilement
            // & si le scroll est vers le bas & si le precedent scroll n'avait pas déjà atteint la fin de la liste*
            // et enfin si le nombre de documents chargés est inférieur au nombre de documents total.
            this.scrollingInProgress = true;
            this.spinner.show('folders-spinner');
            this.lastScrollTop = elem.scrollTop;

            this.datasource
                .listDatas(this.loadedRecordsCount, this._internalConfiguration.listLayout.recordsPerPage, this.getSortInfos(), this.filterInfos, this.generalFilterInfos)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((x) => {
                    x.value.forEach((item) => {
                        this.folders.push(item);
                    });

                    this.loadedFoldersCount = this.folders.length;
                    this.totalFoldersCount = x.totalCount;

                    this.folderCounterEventsService.displayedFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.loadedFoldersCount));
                    this.folderCounterEventsService.totalFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(x.totalCount));

                    this.tableDataSource.paginator = this.paginator;
                    this.fillExpandeedStatusInformation(this.folders, this.tableDataSource.data);
                    this.tableDataSource.data = this.folders;
                    this.cdRef.detectChanges();

                    this.dragContext.clearDragAndDropContext();
                    this.loadedRecordsCount += this._internalConfiguration.listLayout.recordsPerPage;
                    this.paginator._changePageSize(this.loadedRecordsCount);

                    this.scrollingInProgress = false;
                    this.spinner.hide('folders-spinner');
                });
        }
    }

    /**
     * Trie les dossiers du tableau.
     * @param sort : information de tri.
     */
    public sortFolders(sort: Sort): void {
        this.currentSort = sort;

        this.applyFilters();
    }

    /**
     * Affiche le detail des dossiers selectionnés.
     */
    public displayFolder(readonly: boolean): void {
        this.selectionContext.selections
            .map((i) => this.folders[i])
            .forEach((folder) => {
                if (!readonly) {
                    this.foldersService
                        .lock(folder.viewId, folder.id)
                        .pipe(takeUntil(this.componentDestroyed$))
                        .subscribe(
                            (x) => {
                                this.folderDisplayed.emit(new FolderTabInfos(folder.id, folder.name, this.viewsStateService.getSelectedView(), readonly));

                                folder.lockedType = LockedType.LockedByCurrentUser;
                                this.eventsManagerService.emitListFolderUpdatedEvent(folder.viewId, folder.createCopy());
                                this.eventsManagerService.emitFollowedFoldersUpdatedEvent(folder.viewId, folder.createCopy());
                            },
                            (error: HttpErrorResponse) => {
                                this.notificationService.displayWarning('Action impossible', 'Le document est verrouillé par un autre utilisateur.');
                            }
                        );
                } else {
                    this.folderDisplayed.emit(new FolderTabInfos(folder.id, folder.name, this.viewsStateService.getSelectedView(), readonly));
                }
            });
    }

    /**
     * Basculer l'état "lu" du dossier en lu ou non lu.
     * @param readState Nouvel état de lecture du dossier : true pour lu, false pour non lu.
     */
    public toggleReadState(readState: boolean): void {
        this.selectionContext.selections
            .map((i) => this.folders[i])
            .forEach((folder) => {
                this.folderActionsService.toggleReadStatus(folder, readState);
            });
    }

    /**
     * Déplace les dossiers selectionnés d'une bannette vers une autre bannette (selection via une boite de dialogue).
     */
    public moveToBox(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);
        const selectedFolderBoxIds = selectedFolders.map((i) => i.boxId);

        // Retire les ids de bannettes en doublons.
        const uniqueSelectedFolderBoxIds = [...new Set(selectedFolderBoxIds)];

        let sourceBoxId: string = null;

        if (uniqueSelectedFolderBoxIds.length === 1) {
            // Tous les dossiers sont dans la même bannette.
            sourceBoxId = uniqueSelectedFolderBoxIds[0];
        }

        this.dialogService
            .openMoveToBox(sourceBoxId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((result) => {
                if (!!result) {
                    selectedFolders.forEach((folder) => {
                        if (folder.boxId !== result.boxId) {
                            this.folderActionsService.moveToBox(this.viewsStateService.getSelectedView().id, folder, result.boxId);
                            this.foldersCommentsService.add(folder.viewId, folder.id, { text: result.comment }).subscribe((x) => {
                                this.folderCommentStateService.updateFolderComment(folder);
                            });
                        }
                    });
                }
            });
    }

    /**
     * Ouvre la boite de dialogue pour fusionner les dossiers selectionnés
     */
    public merge(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        this.dialogService
            .openMerge(this.viewsStateService.getSelectedView(), selectedFolders)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((result) => {
                if (!!result) {
                    selectedFolders.forEach((folder) => {
                        this.folderActionsService.mergeFolder(folder, result.targetFolder);
                    });
                }
            });
    }

    /**
     * Merge des dossiers selectionné
     */
    public duplicate(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        this.dialogService
            .openDuplicate(selectedFolders)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((result) => {
                if (!!result) {
                    selectedFolders.forEach((folder) => {
                        this.folderActionsService.duplicateFolder(folder, result.duplicateCount);
                    });
                }
            });
    }

    /**
     * Ajoute un fichier à un dossier.
     */
    public addFile(): void {
        if (this.selectionContext.selections.length === 1) {
            const selectedFolder = this.folders[this.selectionContext.selections[0]];
            this.dialogService
                .openAddFile(this.viewsStateService.getSelectedView().id, selectedFolder.id)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((document) => {
                    this.refreshDocuments(selectedFolder.viewId, selectedFolder.id, document.id);
                });
        } else {
            this.notificationService.displayWarning('Opération impossible', "L'ajout de fichier ne peut se faire que pour un dossier. Veuillez ne selectionner qu'un seul dossier");
        }
    }

    public CopyTrackingLink(): void {
        if (this.selectionContext.selections.length === 1) {
            const selectedFolder = this.folders[this.selectionContext.selections[0]];

            this.foldersService.getTrackingLink(selectedFolder.id).subscribe((x) => {
                this.copyToClipboard(x);
            });
        } else {
            this.notificationService.displayWarning(
                'Opération impossible',
                "La copie du lien de suivi ne peut se faire que pour un dossier. Veuillez ne selectionner qu'un seul dossier"
            );
        }
    }

    copyToClipboard(value: string) {
        navigator.clipboard.writeText(value);
    }

    /**
     * Place le dossier en stand-by.
     */
    public setInStandByState(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);
        this.dialogService
            .openSetInStandByState(this.connectedUserStateService.getUserConnected().params.standByDuration)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((result) => {
                if (!!result) {
                    const data = new StandByDatas();
                    data.duration = result.duration;
                    data.endDate = result.endDate;

                    selectedFolders.forEach((folder) => {
                        this.folderActionsService.setInStandByState(folder, data);
                    });
                }
            });
    }

    /**
     * Retire le dossier du mode stand-by
     */
    public removeFromStandByState(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        selectedFolders.forEach((folder) => {
            this.folderActionsService.removeFromStandByState(folder);
        });
    }

    /**
     * Supprime les dossiers selectionnés.
     */
    public deleteFolder(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        if (this.connectedUserStateService.getUserConnected().params.deletionConfirmationDisabled === true) {
            selectedFolders.forEach((folder) => {
                this.folderActionsService.deleteFolder(folder);

                // Met à jour les compteurs
                this.totalFoldersCount = this.totalFoldersCount - 1;
                this.loadedFoldersCount = this.loadedFoldersCount - 1;
                this.loadedRecordsCount = this.loadedRecordsCount - 1;

                this.folderCounterEventsService.displayedFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.loadedFoldersCount));
                this.folderCounterEventsService.totalFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.totalFoldersCount));
            });
        } else {
            this.dialogService
                .openDeleteFolder(selectedFolders)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((isConfirm) => {
                    if (isConfirm) {
                        selectedFolders.forEach((folder) => {
                            this.folderActionsService.deleteFolder(folder);

                            // Met à jour les compteurs
                            this.totalFoldersCount = this.totalFoldersCount - 1;
                            this.loadedFoldersCount = this.loadedFoldersCount - 1;
                            this.loadedRecordsCount = this.loadedRecordsCount - 1;

                            this.folderCounterEventsService.displayedFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.loadedFoldersCount));
                            this.folderCounterEventsService.totalFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.totalFoldersCount));
                        });
                    }
                });
        }
    }

    public UnlockFolder(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        if (selectedFolders.length > 1) {
            this.notificationService.displayWarning('Demande de déverrouillage', 'Vous ne pouvez pas faire plusieurs demande de déverrouillage à la fois.');
        } else {
            selectedFolders.forEach((folder) => {
                this.foldersService.requestUnlockFolder(folder.viewId, folder.id).subscribe(() => {
                    this.notificationService.displayInfo(
                        'Demande de déverrouillage',
                        "La demande de déverrouillage du dossier a été envoyé à l'utilisateur concerné. Vous serez notifié de sa réponse dans la minute qui suit."
                    );
                });
            });
        }
    }

    /**
     * Archive le dossier.
     */
    public Archive(): void {
        this.selectionContext.selections
            .map((i) => this.folders[i])
            .forEach((folder) => {
                this.folderActionsService.archive(folder);
            });
    }

    /**
     * Place le dossier en attente d'archivage.
     */
    public SetInWaitingArchivingState(): void {
        this.selectionContext.selections
            .map((i) => this.folders[i])
            .forEach((folder) => {
                this.folderActionsService.SetInWaitingArchivingState(folder);
            });
    }

    /**
     * Retire le dossier de l'état en attente d'archivage.
     */
    public removeFromWaitingArchivingState(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        selectedFolders.forEach((folder) => {
            this.folderActionsService.removeFromWaitingArchivingState(folder);
        });
    }

    /**
     * Suit le dossier.
     */
    public followFolder(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        selectedFolders.forEach((folder) => {
            this.folderActionsService.followFolder(folder);
        });
    }

    /**
     * Arrête le suivi du dossier.
     */
    public stopFollowFolder(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        selectedFolders.forEach((folder) => {
            this.folderActionsService.stopFollowFolder(folder);
        });
    }

    /**
     * Plie ou déplie une ligne du tableau.
     * @param row: Instance de la ligne.
     */
    expandOrCollapseRow(row: Folder) {
        if (row.expanded === false) {
            if (this.allowMultipleExpandRows) {
                row.expanded = true;
            } else {
                this.tableDataSource.data.forEach((element) => {
                    element.expanded = false;
                });
                row.expanded = true;
            }
        } else {
            row.expanded = false;
        }
    }

    /**
     * Levé lorsque l'utilisateur ouvre le menu contextuel.
     * @param event : Événement d'ouverture du menu contextuel
     */
    public onContextualMenuOpen(event: ContextualMenuOpenEvent): void {
        if (this.selectionContext.selections.indexOf(event.index) === -1) {
            // Si la ligne pour laquelle l'utilisateur a affichée le menu contextuel n'est pas sélectionnée --> l'ajoute à la selection.
            this.selectionContext.handleSelection(event.sourceEvent, event.index);
        }
    }

    /**
     * Levé lorsque l'utilisateur selectionne une action du menu contextuel.
     * @param event : Événement de selection d'une action du menu contextuel.
     */
    public onContextualMenuSelect(event: ContextualMenuSelectEvent): void {
        switch (event.actionType) {
            case ContextualActionType.markAsRead:
                this.toggleReadState(true);
                break;
            case ContextualActionType.markAsUnread:
                this.toggleReadState(false);
                break;
            case ContextualActionType.addFile:
                this.addFile();
                break;
            case ContextualActionType.deleteFolder:
                this.deleteFolder();
                break;
            case ContextualActionType.displayFolder:
                this.displayFolder(true);
                break;
            case ContextualActionType.moveToBox:
                this.moveToBox();
                break;
            case ContextualActionType.merge:
                this.merge();
                break;
            case ContextualActionType.duplicate:
                this.duplicate();
                break;
            case ContextualActionType.setInStandByState:
                this.setInStandByState();
                break;
            case ContextualActionType.removeFromStandByState:
                this.removeFromStandByState();
                break;
            case ContextualActionType.CopyTrackingLink:
                this.CopyTrackingLink();
                break;
            case ContextualActionType.unlockFolder:
                this.UnlockFolder();
                break;
            case ContextualActionType.archive:
                this.Archive();
                break;
            case ContextualActionType.SetInWaitingArchivingState:
                this.SetInWaitingArchivingState();
                break;
            case ContextualActionType.RemoveFromWaitingArchivingState:
                this.removeFromWaitingArchivingState();
                break;
            case ContextualActionType.followFolder:
                this.followFolder();
                break;
            case ContextualActionType.stopFollowFolder:
                this.stopFollowFolder();
                break;
        }
    }

    /**
     * Appelée lorsque l'utilisateur double-clique sur un élément de la liste.
     * @param event : Evenment clic de la souris.
     * @param index : Index de l'élément cliqué.
     */
    onMouseDoubleClick(event: MouseEvent, index: number) {
        this.selectionContext.clearSelection();
        this.selectionContext.selections = [index];
        // Garde une trace du dernier élément cliqué.
        this.selectionContext.lastSingleSelection = index;
        if (this.connectedUserStateService.hasRight(Rights.FolderEdit) && this.viewsStateService.getSelectedView().isArchive === false) {
            this.displayFolder(false);
        } else {
            // Si l'utilisateur n'a pas le droit d'edition des dossiers ou s'il s'agit d'une vue Arrchive --> affichage du dossier en consultation seule.
            this.displayFolder(true);
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur un élément de la liste.
     * @param event : Evenment clic de la souris.
     * @param index : Index de l'élément cliqué.
     */
    onMouseClick(event: MouseEvent, index: number) {
        this.selectionContext.onMouseClick(event, index);
    }

    /**
     * Traite les événements clavier de type KeyDown sur le document.
     * @param event Événement du clavier.
     */
    @HostListener('document:keydown', ['$event'])
    private handleKeyboardEvent(event: KeyboardEvent) {
        this.dragContext.handleKeyboardEvent(event);

        if (event.cancelBubble === false) {
            this.selectionContext.handleKeyboardEvent(event);
        }
    }

    /**
     * Traite les événements souris de type Click sur le document.
     * @param event Événement de la souris.
     */
    @HostListener('document:click', ['$event'])
    private clickout(event) {
        if (this.selectionContext.selections.length && !this.foldersTableRef.nativeElement.contains(event.target)) {
            // Si la selection n'est pas vide et que l'utilisateur clique en dehors du containeur des éléments pouvant être glisser/déplacer, alors vide la selection.
            this.selectionContext.clearSelection();
        }
    }

    /**
     * Levé lorsque une opération de glisser (drag) d'un dossier commence.
     * @param event : Événement de glisser (drag) d'un dossier.
     */
    onDragStarted(event: CdkDragStart, folder: Folder, index: number): void {
        if (this.selectionContext.selections.length === 0) {
            // Si la selection est vide, alors l'élément glissé est l'unique élément de la selection.
            let selectedFolder = this.folders[index];
            if (selectedFolder.expanded === true) {
                selectedFolder.expanded = false;
            }
        } else {
            this.selectionContext.selections.forEach((index) => {
                let selectedFolder = this.folders[index];
                if (selectedFolder.expanded === true) {
                    selectedFolder.expanded = false;
                }
            });
        }

        this.dragContext.onDragStarted(event, folder, index);
    }

    /**
     * Levé lorsque une opération de 'glisser' (drag) d'un dossier se termine.
     * @param event : Événement de 'glisser' (drag) d'un dossier.
     */
    onDragEnded(event: CdkDragEnd, folder: Folder) {
        this.dragContext.onDragEnded(event, folder);
    }

    /**
     * Emis lorsque l'utilisateur dépose un élément dans la liste des dossiers
     * @param event : Événement de glisser/deposer
     */
    onDrop(event: CdkDragDrop<Folder>): void {
        this.dragContext.onDrop(event);
    }

    public BuildPreviews(): string[] {
        let previews = [];
        this.selectionContext.selections.forEach((index) => {
            let folder = this.folders[index];
            previews.push(`${folder.name} - ${folder.id}`);
        });

        return previews;
    }

    /**
     * Indique si l'élément a été déposé par drag&drop.
     * @param index : index de l'élément à tester
     * @returns True si l'élément a été déposé, ou false autrement.
     */
    isDropped(index: number): boolean {
        return this.dragContext.isDropped(index);
    }

    /**
     * Fonction utilisé pour  traquer les dossiers afin de déterminer les dossiers supprimés ou ajouter de la liste.
     * @param index : Index du dossier dans la liste.
     * @param item : Instance du dossier.
     * @returns : Une identification du dossier utilisé pour la comparaison.
     */
    public trackFolder(index: number, item: Folder): string {
        return `${item.id}`;
    }

    /**
     * Retourne les Ids de tous les dossiers filtrés.
     * Implemntation de IAllSelectable.
     * @returns Un tableau d'Ids.
     */
    public getAllIds() {
        return this.folders.map((folder: Folder, index: number) => index);
    }

    /**
     * Indique si l'élément specifié est selectionné.
     * @param index : index de l'élément à tester
     * @returns True si l'élément est selectionné, ou false autrement.
     */
    isSelected(index: number): boolean {
        return this.selectionContext.isSelected(index);
    }

    /**
     * Récupère les enregistrements correspondants au id déplacés.
     * Implémentation de IDragable<T>
     * @param indices : Liste des indices des enregistrements glissés.
     */
    public getDragedRecords(indices: number[]) {
        return indices.map((i) => this.folders[i]);
    }

    /**
     * Retourne l'ensemble des enregistrements affichés dans le tableau.
     * Implémentation de IDragable<T>
     */
    public getRecords() {
        return this.folders.filter((folder, index) => this.selectionContext.selections.indexOf(index) === -1);
    }

    /**
     * Execute l'action associée au dépôt de l'enregistrement.
     * Implémentation de IDragable<T>
     * @param record : Enregistrement déposé.
     * @param destinationFolder : Enregistrement destination.
     */
    public executeOnDropAction(folder, destinationFolder) {
        this.folderActionsService.mergeFolder(folder, destinationFolder);
    }

    /**
     * Copie le texte sur lequel un clic-droit a été réalisé dans le presse-papier.
     * @param event : Événement clic-droit de la souris.
     */
    public copyTextIntoClipboard(event: MouseEvent): void {
        // Récupère le noeud HTML sur lequel l'utilisateur a fait un clic-droit.
        const node = event.target as Node;

        // Récupère le texte du noeud HTML
        const cellText = node.textContent;
        if (cellText) {
            navigator.clipboard
                .writeText(cellText)
                .then(() => this.notificationService.displayInfo('Presse-papier.', `Le texte \'${cellText}\' a été copié`))
                .catch((error) => this.notificationService.displayError('Presse-papier.', `Le texte n'a pas pu être été copié dans le presse-papier : \'${error}\'`));
        }
    }

    public resetScroll() {
        this.currentScrollTop = 0;
        this.currentScrollLeft = 0;
        this.scrollingInProgress = false;
        this.lastScrollTop = 0;
        if (this.resultsContainerElementRef) {
            var nativ = this.resultsContainerElementRef.nativeElement;
            nativ.scrollTop = this.currentScrollTop;
            nativ.scrollLeft = this.currentScrollLeft;
        }
    }

    /**
     * Retourne les informations sur le tri à envoyer au serveur.
     */
    private getSortInfos(): SortInfos {
        if (this.currentSort) {
            var columnDefinition = this._internalConfiguration.attributeDefinitions.find((x) => x.propertyCode === this.currentSort.active);

            if (columnDefinition) {
                return {
                    columnName: this.currentSort.active,
                    direction: this.currentSort.direction.toString(),
                    columnType: columnDefinition.type,
                };
            } else {
                // La colonne spécifiée ne correspond pas à un attribut de la vue (problème de configuration)
                return null;
            }
        } else {
            // Pas de tri --> recupération des données initiales
            return null;
        }
    }

    private refreshDocuments(viewId: number, folderId: string, selectedDocumentId: string = null) {
        this.eventsManagerService.emitHistorySectionUpdatedEvent(viewId, folderId);

        this.folderDocumentsService.list(viewId, folderId).subscribe((documents) => {
            this.eventsManagerService.emitDocumentsUpdatedEvent(folderId, documents, selectedDocumentId);
        });

        this.eventsManagerService.emitListFolderUpdatedEvent(viewId, folderId);
        this.eventsManagerService.emitFollowedFoldersUpdatedEvent(viewId, folderId);
    }

    private fillExpandeedStatusInformation(folders: Folder[], folderWithExpandedInfos: Folder[]) {
        if (folderWithExpandedInfos) {
            folderWithExpandedInfos
                .filter((x) => x.expanded == true)
                .forEach((y) => {
                    let folder = folders.find((z) => z.id === y.id);

                    if (folder) {
                        folder.expanded = true;
                    }
                });
        }
    }

    /**
     * Appellée lorsqu'une colonne est redimensionnée.
     * @param column : Colonne
     * @param width
     */
    public onColumnResizing(column: ColumnDefinition, width: number) {
        console.log(`Column ${column.propertyCode} resized to ${width}`);

        if (this._internalConfiguration.listLayout.isLayoutMustFitScreenSize) {
            // Cas d'une mise en page s'adaptant à l'écran.
            const columnIndex = this.displayedColumnsDefinitions.findIndex((x) => x.propertyCode === column.propertyCode);

            if (columnIndex === this.displayedColumnsDefinitions.length - 1) {
                // Il s'agit de la dernière colonne --> on ne peut pas la redimensionner.
            } else {
                // On détermine la colonne qui suit celle qui est redimensionnée
                const nextColumn = this.displayedColumnsDefinitions[columnIndex + 1];
                let delta = width - column.size;

                if (nextColumn.size - delta < 1) {
                    // On ne peut pas réduire la largeur d'une colonne à moins de 1%.
                    delta = nextColumn.size - 1;
                }

                // On agrandit la colonne redimensionnée et on retrecit d'autant la
                column.size = column.size + delta;
                nextColumn.size = nextColumn.size - delta;
            }
        } else {
            // Cas d'une mise en page autorisant une scrollbar horizontale
            column.size = width;
        }

        this.cdRef.markForCheck();
        this.cdRef.detectChanges();
    }

    /**
     * Appellée lorsque le redimensionnement d'une colonne est terminé.
     * @param column
     */
    public onColumnResized(column: ColumnDefinition) {
        // Sauvegarde de la taille dans le local storage.
        localStorage.setItem(
            this.getLocalStorageKey(this._internalConfiguration.viewId, this._internalConfiguration.listLayout.id),
            JSON.stringify(this.displayedColumnsDefinitions)
        );
    }

    /**
     * Récupère la clé utilisée pour stockée la largeur des colonnes de la liste.
     * @param viewId : Id de la vue.
     * @param viewId : Id de la mise en page.
     * @returns Clé pour stockée le contexte.
     */
    private getLocalStorageKey(viewId: number, listLayoutId: number) {
        return `foldersList-columns-${this.connectedUserStateService.getUserConnected().userId}-${viewId}-${listLayoutId}`;
    }

    /**
     * Retourne une valeur indiquant si la mise en page du serveur a subi des modifications par rapport à la mise en page du local storage.
     * @param serverLayout : Mise en page du serveur.
     * @param localLayout : Mise en page locale.
     * @returns : true, si la mise en page du serveur a subi des modifications par rapport à la mise en page locale; false autrement.
     */
    private hasLayoutBeModified(serverLayout: ColumnDefinition[], localLayout: ColumnDefinition[]) {
        if (serverLayout.length != localLayout.length) {
            return true;
        }

        for (let index = 0; index < serverLayout.length; index++) {
            if (!ColumnDefinition.Equals(serverLayout[index], localLayout[index])) {
                return true;
            }
        }

        return false;
    }
}
